import { Button } from '@mdvip/shared/components/ui/button';
import { Separator } from '@mdvip/shared/components/ui/separator';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { Check, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { detectNetwork } from '@/lib/payments/paymentAccount.format';
import type {
  BillingAddressValues,
  CardNetwork,
  PaymentAccountFormValues,
  PaymentTab,
} from '@/lib/payments/paymentAccount.types';

const NETWORK_LABEL: Record<CardNetwork, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  unknown: 'Card',
};

const maskLast4 = (value: string) => `···· ${value.replace(/\D/g, '').slice(-4)}`;
const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

function ReviewSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      <dl className="space-y-3">{children}</dl>
    </div>
  );
}

// Stacked label-over-value (matches the design's confirm rows): muted xs label, medium value.
function ReviewRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

interface PaymentReviewScreenProps {
  activeTab: PaymentTab;
  autoPayAmount: number | null;
  autoPayPlan: string | null;
  billing: BillingAddressValues;
  isSubmitting: boolean;
  onMakeChanges: () => void;
  onConfirm: () => void;
}

/** Screen 2: read-only two-column summary of the entered account + AutoPay + billing, then confirm. */
export function PaymentReviewScreen({
  activeTab,
  autoPayAmount,
  autoPayPlan,
  billing,
  isSubmitting,
  onMakeChanges,
  onConfirm,
}: PaymentReviewScreenProps) {
  const { t } = useTranslation('members');
  const { formatCurrency } = useCurrency();
  const { getValues } = useFormContext<PaymentAccountFormValues>();
  const values = getValues();
  const isCard = activeTab === 'credit-card';

  const yn = (flag: boolean) =>
    flag ? t('payments.addAccount.reviewScreen.yes') : t('payments.addAccount.reviewScreen.no');
  const autoPayOn = isCard ? values.card.autoPay : values.bank.autoPay;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2">
        {isCard ? (
          <ReviewSection title={t('payments.addAccount.reviewScreen.sectionCard')}>
            <ReviewRow
              label={t('payments.addAccount.reviewScreen.card')}
              value={`${NETWORK_LABEL[detectNetwork(values.card.cardNumber)]} ${maskLast4(values.card.cardNumber)}`}
            />
            <ReviewRow label={t('payments.addAccount.reviewScreen.expiration')} value={values.card.expiry} />
            <ReviewRow label={t('payments.addAccount.reviewScreen.hsa')} value={yn(values.card.isHsa)} />
            <ReviewRow label={t('payments.addAccount.reviewScreen.makeDefault')} value={yn(values.card.makeDefault)} />
          </ReviewSection>
        ) : (
          <ReviewSection title={t('payments.addAccount.reviewScreen.sectionBank')}>
            <ReviewRow label={t('payments.addAccount.reviewScreen.bank')} value={values.bank.bankName} />
            <ReviewRow
              label={t('payments.addAccount.reviewScreen.accountNumber')}
              value={maskLast4(values.bank.accountNumber)}
            />
            <ReviewRow
              label={t('payments.addAccount.reviewScreen.accountType')}
              value={capitalize(values.bank.accountType)}
            />
            <ReviewRow label={t('payments.addAccount.reviewScreen.hsa')} value={yn(values.bank.isHsa)} />
            <ReviewRow label={t('payments.addAccount.reviewScreen.makeDefault')} value={yn(values.bank.makeDefault)} />
          </ReviewSection>
        )}

        <ReviewSection title={t('payments.addAccount.reviewScreen.sectionAutoPay')}>
          <ReviewRow label={t('payments.addAccount.reviewScreen.autoPayThisAccount')} value={yn(autoPayOn)} />
          <ReviewRow
            label={t('payments.addAccount.reviewScreen.amount')}
            value={autoPayAmount === null ? '—' : formatCurrency(autoPayAmount)}
          />
          <ReviewRow label={t('payments.addAccount.reviewScreen.paymentPlan')} value={autoPayPlan ?? '—'} />
        </ReviewSection>
      </div>

      <Separator />

      <ReviewSection title={t('payments.addAccount.reviewScreen.sectionBilling')}>
        {billing.name.trim() === '' ? (
          <p className="text-sm text-muted-foreground">{t('payments.addAccount.reviewScreen.noBilling')}</p>
        ) : (
          <>
            <ReviewRow
              label={
                isCard
                  ? t('payments.addAccount.reviewScreen.nameOnCard')
                  : t('payments.addAccount.reviewScreen.accountHolderName')
              }
              value={billing.name}
            />
            <ReviewRow
              label={t('payments.addAccount.reviewScreen.address')}
              value={
                <span className="leading-relaxed">
                  {billing.line1}
                  {billing.line2 ? `, ${billing.line2}` : ''}
                  <br />
                  {`${billing.city}, ${billing.state} ${billing.zip}`}
                </span>
              }
            />
          </>
        )}
      </ReviewSection>

      <div className="flex gap-3 [&>button]:flex-1">
        <Button type="button" variant="outline" onClick={onMakeChanges}>
          <Pencil className="size-3.5" /> {t('payments.addAccount.reviewScreen.makeChanges')}
        </Button>
        <Button type="button" onClick={onConfirm} disabled={isSubmitting}>
          <Check className="size-3.5" /> {t('payments.addAccount.reviewScreen.confirmSubmit')}
        </Button>
      </div>
    </div>
  );
}
